'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import RSVPForm from './RSVPForm';
import SuccessModal from './SuccessModal';
import BlessingCard from './BlessingCard';
import RSVPStats from './RSVPStats';

interface GuestInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  guestCount: number;
  plusOneName: string;
  mealPreference: string;
  dietaryRestrictions: string;
  attendingCeremony: boolean;
  attendingReception: boolean;
  blessingMessage: string;
  allowPublicBlessing: boolean;
}

interface Blessing {
  id: number;
  name: string;
  message: string;
  date: string;
}

const RSVPInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedGuestName, setSubmittedGuestName] = useState('');
  const [stats, setStats] = useState({
    totalResponses: 0,
    ceremonyCount: 0,
    receptionCount: 0,
    blessingsCount: 0,
  });
  const [blessings, setBlessings] = useState<Blessing[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    setIsHydrated(true);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data: responses, error } = await supabase
        .from('rsvp_responses')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (responses && Array.isArray(responses)) {
        // Calculate stats
        const totalResponses = responses.length;
        const ceremonyCount = responses.filter(r => r.attending_ceremony).length;
        const receptionCount = responses.filter(r => r.attending_reception).length;
        const blessingsCount = responses.filter(
          r => r.blessing_message && r.allow_public_blessing
        ).length;

        setStats({
          totalResponses,
          ceremonyCount,
          receptionCount,
          blessingsCount,
        });

        // Get public blessings
        const publicBlessings = responses
          .filter(r => r.blessing_message && r.allow_public_blessing)
          .slice(0, 4)
          .map((r, index) => ({
            id: index + 1,
            name: `${r.first_name} ${r.last_name}`,
            message: r.blessing_message || '',
            date: new Date(r.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
          }));

        setBlessings(publicBlessings);
      }
    } catch (error) {
      console.error('Error fetching RSVP data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRSVPSubmit = async (data: GuestInfo) => {
    try {
      const { error } = await supabase.from('rsvp_responses').insert({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone,
        guest_count: data.guestCount,
        plus_one_name: data.plusOneName || null,
        meal_preference: data.mealPreference,
        dietary_restrictions: data.dietaryRestrictions || null,
        attending_ceremony: data.attendingCeremony,
        attending_reception: data.attendingReception,
        blessing_message: data.blessingMessage || null,
        allow_public_blessing: data.allowPublicBlessing,
      });

      if (error) throw error;

      setSubmittedGuestName(data.firstName);
      setShowSuccessModal(true);
      
      // Refresh data
      fetchData();
      
      // Clear localStorage draft
      localStorage.removeItem('rsvp_draft');
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      alert('Failed to submit RSVP. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  if (!isHydrated || loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-surface rounded w-1/2 mx-auto"></div>
            <div className="h-6 bg-surface rounded w-3/4 mx-auto"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-32 bg-surface rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {/* Stats Section */}
        <div>
          <RSVPStats
            totalResponses={stats.totalResponses}
            attendingCeremony={stats.ceremonyCount}
            attendingReception={stats.receptionCount}
            blessingsReceived={stats.blessingsCount}
          />
        </div>

        {/* RSVP Form Section */}
        <div>
          <RSVPForm onSubmit={handleRSVPSubmit} />
        </div>

        {/* Community Blessings Section */}
        {blessings.length > 0 && (
          <div>
            <div className="text-center mb-8">
              <h2 className="font-playfair text-3xl sm:text-4xl font-semibold text-foreground mb-3">
                Community Blessings
              </h2>
              <p className="font-inter text-base text-muted-foreground max-w-2xl mx-auto">
                Read the beautiful messages and prayers shared by our loved ones as they celebrate our union
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {blessings.map((blessing) => (
                <BlessingCard key={blessing.id} blessing={blessing} />
              ))}
            </div>
          </div>
        )}
      </div>

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
        guestName={submittedGuestName}
      />
    </>
  );
};

export default RSVPInteractive;